import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import {
  IdentifierSchema,
  MemberEntitySchema,
  ProjectSchema,
  ProjectSelectorTableProps,
} from "@/types";
import { projectColumns, projectNameColumn } from "@/util";
import {
  getProjectApiStore,
  identifierSaveStore,
  memberApiStore,
  permissionStore,
  projectStore,
  sessionStore,
  teamApiStore,
  teamStore,
} from "@/hooks";
import { Routes } from "@/router";
import { SelectorTable, IconButton, Typography } from "@/components/common";
import { InviteMemberModal } from "@/components/members";


/**
 * A table for selecting projects.
 */
const __default__ = {
  name: "ProjectSelectorTable",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    teamOnly: { type: Boolean },
    open: { type: Boolean },
    minimal: { type: Boolean }
  },
  emits: ["selected"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const currentRoute = useRoute();

const selected = ref<IdentifierSchema | undefined>();
const projectInviteId = ref<string>();

const columns = computed(() =>
  props.minimal ? [projectNameColumn] : projectColumns
);
const rows = computed(() =>
  props.teamOnly ? teamStore.allProjects : projectStore.allProjects
);

const entity = computed(
  () =>
    ({
      entityId: projectInviteId.value,
      entityType: "PROJECT",
    }) as MemberEntitySchema
);

const selectedItems = computed({
  get() {
    return selected.value ? [selected.value] : [];
  },
  set(items: IdentifierSchema[]) {
    selected.value = items[0];
    emit("selected", items[0]);
  },
});

/**
 * Loads all projects.
 */
function handleReload() {
  selected.value = undefined;
  projectInviteId.value = undefined;

  if (props.teamOnly) {
    teamApiStore.handleLoadProjects();
  } else {
    getProjectApiStore.handleLoadProjects();
  }
}

/**
 * Removes the current user from the project.
 * @param project - The project to leave.
 */
function handleLeave(project: IdentifierSchema) {
  const member = sessionStore.getCurrentMember(project);

  if (!member) return;

  memberApiStore.handleDelete(member, project);
}

/**
 * Whether the current user can edit the project.
 * @param project - The project to check.
 * @returns Whether the current user can edit the project.
 */
function isEditable(project: IdentifierSchema): boolean {
  return permissionStore.isAllowed("project.edit", project);
}

/**
 * Whether the current user can delete the project.
 * @param project - The project to check.
 * @returns Whether the current user can delete the project.
 */
function isDeletable(project: IdentifierSchema): boolean {
  return permissionStore.isAllowed("project.delete", project);
}

/**
 * Loads all projects when opened.
 */
watch(
  () => props.open,
  (open) => {
    if (!open) return;

    handleReload();
  }
);

watch(
  () => currentRoute.path,
  (path) => {
    if (path !== Routes.PROJECT_CREATOR) return;

    handleReload();
  }
);

onMounted(() => {
  handleReload();
});

return (_ctx: any,_cache: any) => {
  const _component_q_td = _resolveComponent("q-td")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(SelectorTable), {
      selected: selectedItems.value,
      "onUpdate:selected": _cache[0] || (_cache[0] = ($event: any) => ((selectedItems).value = $event)),
      minimal: props.minimal,
      addable: "",
      editable: isEditable,
      deletable: isDeletable,
      loading: _unref(getProjectApiStore).loading,
      columns: columns.value,
      rows: rows.value,
      "row-key": "projectId",
      "item-name": "project",
      icons: {
      add: 'project-add',
      edit: 'project-edit',
      delete: 'project-delete',
    },
      "data-cy": "table-project",
      "custom-cells": ['name'],
      onRefresh: handleReload,
      "onRow:add": _cache[1] || (_cache[1] = ($event: any) => (_unref(identifierSaveStore).selectIdentifier(undefined, 'save'))),
      "onRow:edit": _cache[2] || (_cache[2] = ($event: any) => (_unref(identifierSaveStore).selectIdentifier($event, 'save'))),
      "onRow:delete": _cache[3] || (_cache[3] = ($event: any) => (_unref(identifierSaveStore).selectIdentifier($event, 'delete')))
    }, {
      "body-cell-name": _withCtx(({ row }: { row: ProjectSchema }) => [
        _createVNode(_component_q_td, { style: {"max-width":"400px"} }, {
          default: _withCtx(() => [
            _createVNode(_unref(Typography), {
              value: row.name
            }, null, 8, ["value"]),
            _createVNode(_unref(Typography), {
              value: row.description,
              secondary: "",
              el: "div",
              class: "text-ellipsis"
            }, null, 8, ["value"])
          ]),
          _: 2
        }, 1024)
      ]),
      "cell-actions": _withCtx(({ row }: { row: ProjectSchema }) => [
        (isEditable(row))
          ? (_openBlock(), _createBlock(_unref(IconButton), {
              key: 0,
              small: props.minimal,
              tooltip: `Invite to ${row.name}`,
              icon: "invite",
              "data-cy": "button-project-invite",
              onClick: ($event: any) => (projectInviteId.value = row.projectId)
            }, null, 8, ["small", "tooltip", "onClick"]))
          : _createCommentVNode("", true),
        (row.members.length > 1)
          ? (_openBlock(), _createBlock(_unref(IconButton), {
              key: 1,
              small: props.minimal,
              icon: "leave",
              tooltip: "Leave project",
              "data-cy": "button-selector-leave",
              onClick: ($event: any) => (handleLeave(row))
            }, null, 8, ["small", "onClick"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["selected", "minimal", "loading", "columns", "rows"]),
    _createVNode(_unref(InviteMemberModal), {
      open: !!projectInviteId.value,
      entity: entity.value,
      onClose: _cache[4] || (_cache[4] = ($event: any) => (projectInviteId.value = undefined)),
      onSubmit: _cache[5] || (_cache[5] = ($event: any) => (projectInviteId.value = undefined))
    }, null, 8, ["open", "entity"])
  ], 64))
}
}

})